@if (!view().rows.length) {
<div
  class="grid place-items-center rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 py-16">
  <div class="flex max-w-md flex-col items-center gap-3 px-6 text-center">
    <span
      class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-ink-soft/50">
      <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
    </span>
    <div class="text-base font-medium text-slate-700 dark:text-ink-soft">{{ agentName() }} never looked anything up</div>
    <div class="text-xs text-slate-400">Nothing in this demo needed the knowledge base — either no question called for
      it, or the call ended first.</div>
  </div>
</div>
} @else {
<div class="space-y-3">

  <!-- what this tab is -->
  <div class="rounded-xl border border-[#EAEAEA] bg-slate-50 px-4 py-3 dark:border-line dark:bg-white/5">
    <h3 class="text-xs font-semibold text-[#141437] dark:text-white">What this page shows</h3>
    <p class="mt-1 text-xs leading-relaxed text-slate-500 dark:text-ink-soft/70">
      Every time the prospect asked something, {{ agentName() }} searched a knowledge base before answering. Each card below is one
      of those moments: <strong class="font-medium text-slate-600 dark:text-ink-soft">what it searched for</strong>,
      <strong class="font-medium text-slate-600 dark:text-ink-soft">which pieces of knowledge came back</strong>,
      <strong class="font-medium text-slate-600 dark:text-ink-soft">what it then said</strong>, and
      <strong class="font-medium text-slate-600 dark:text-ink-soft">which slide went on screen</strong>. Use it to
      find the document behind a wrong answer.
    </p>
  </div>

  <!-- the call at a glance -->
  <div class="grid grid-cols-2 gap-3 xl:grid-cols-4">
    <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
      <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{ view().summary.lookups
        }}</div>
      <div class="mt-0.5 text-[11px] text-slate-400">Questions looked up</div>
    </div>
    <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
      <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{ view().summary.hits }}
      </div>
      <div class="mt-0.5 text-[11px] text-slate-400">Pieces of knowledge found</div>
    </div>
    <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
      <div class="text-lg font-semibold leading-5 tabular-nums"
        [class]="view().summary.duplicates ? 'text-amber-600 dark:text-amber-300' : 'text-[#141437] dark:text-white'">{{
        view().summary.duplicates }}</div>
      <div class="mt-0.5 text-[11px] text-slate-400">Duplicates among them</div>
    </div>
    <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
      <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{
        msText(view().summary.ms) }}</div>
      <div class="mt-0.5 text-[11px] text-slate-400">Time spent searching</div>
    </div>
  </div>

  <!-- what the numbers mean -->
  @if (view().summary.duplicates || view().summary.emptySearches || view().summary.notConsulted) {
  <div class="space-y-2">
    @if (view().summary.duplicates) {
    <div
      class="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs leading-relaxed text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
      <svg class="mt-px h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
      <span><strong>{{ view().summary.duplicates }} duplicate {{ view().summary.duplicates === 1 ? 'piece' : 'pieces' }}
          of knowledge.</strong> The same passage came back more than once because it is stored under more than one
        document — usually the same file uploaded twice. Each duplicate takes a slot a different passage could have
        used, so {{ agentName() }} had less to work with than the numbers suggest.</span>
    </div>
    }
    @if (view().summary.emptySearches) {
    <div
      class="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs leading-relaxed text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
      <svg class="mt-px h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.35-4.35" />
      </svg>
      <span><strong>{{ view().summary.emptySearches }} of {{ view().summary.searches }} searches came back
          empty.</strong> When a narrowed search finds nothing, {{ agentName() }} searches again without the filter, so the answer
        still lands — but a filter that never matches anything is doing no work.</span>
    </div>
    }
    @if (view().summary.notConsulted) {
    <div
      class="flex items-start gap-2 rounded-xl border border-[#EAEAEA] bg-slate-50 px-4 py-2.5 text-xs leading-relaxed text-slate-500 dark:border-line dark:bg-white/5 dark:text-ink-soft/70">
      <svg class="mt-px h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
      <span><strong>{{ view().summary.notConsulted }} of {{ view().summary.lookups }} questions never reached the
          knowledge base.</strong> Questions about fees, protection and terms are always answered from one standard fee
        sheet, on purpose, so those numbers can never drift.</span>
    </div>
    }
  </div>
  }

  <!-- controls -->
  <div class="flex flex-wrap items-center justify-end gap-2">
    <button type="button"
      class="rounded-lg border border-[#EAEAEA] bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600 dark:border-line dark:bg-surface-1 dark:text-ink-soft dark:hover:border-line-strong"
      (click)="expandAll()">Expand all</button>
    <button type="button"
      class="rounded-lg border border-[#EAEAEA] bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600 dark:border-line dark:bg-surface-1 dark:text-ink-soft dark:hover:border-line-strong"
      (click)="collapseAll()">Collapse all</button>
    <button type="button" [attr.aria-pressed]="technical()"
      class="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium transition"
      [class]="technical()
        ? 'border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-500/50 dark:bg-indigo-500/10 dark:text-indigo-300'
        : 'border-[#EAEAEA] bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600 dark:border-line dark:bg-surface-1 dark:text-ink-soft dark:hover:border-line-strong'"
      (click)="toggleTechnical()">
      <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path
          d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
      Technical details
    </button>
  </div>

  <!-- the lookups -->
  @for (row of view().rows; track $index; let li = $index) {
  <div class="overflow-hidden rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1">

    <button type="button"
      class="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-slate-50 dark:hover:bg-white/5"
      (click)="toggle(li)">
      <span
        class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400 transition dark:bg-white/5">
        <svg class="h-3.5 w-3.5 transition-transform" [class.rotate-90]="isOpen(li)" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span class="text-[11px] tabular-nums text-slate-400">{{ row.time }}</span>
          <span class="text-sm font-medium text-[#141437] dark:text-white">The prospect asked about “{{ row.query
            }}”</span>
        </div>
        <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="text-[11px] text-slate-500 dark:text-ink-soft/70">{{ row.headline }}</span>
          @if (row.duplicates) {
          <span
            class="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:bg-amber-500/10 dark:text-amber-300">{{
            row.duplicates }} duplicate</span>
          }
          @if (row.audit) {
          <span
            class="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">checked</span>
          }
          @if (technical()) {
          <span class="text-[10px] tabular-nums text-slate-400">{{ row.searchCount }} searches · {{ msText(row.ms)
            }}</span>
          }
        </div>
      </div>
    </button>

    @if (isOpen(li)) {
    <div
      class="grid gap-px border-t border-[#EAEAEA] bg-slate-100 dark:border-line dark:bg-black/20 xl:grid-cols-[1fr_340px]">

      <!-- left: what it searched, and what came back -->
      <div class="space-y-px">
        @if (!row.consulted) {
        <div
          class="bg-white px-4 py-3 text-[11px] leading-relaxed text-slate-500 dark:bg-surface-1 dark:text-ink-soft/60">
          No search ran for this question. Fees, protection, approvals and terms are always answered from one standard
          fee sheet, so those numbers can never drift with the knowledge base.
        </div>
        }
        @for (s of row.searches; track s.index) {
        <div class="bg-white px-4 py-3 dark:bg-surface-1">

          <!-- what this search did, in a sentence -->
          <div class="flex items-start gap-2.5">
            <span
              class="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-[10px] font-semibold text-indigo-500 tabular-nums dark:bg-indigo-500/10 dark:text-indigo-300">{{
              s.index }}</span>
            <div class="min-w-0 flex-1">
              <p class="flex flex-wrap items-baseline gap-x-1.5 text-xs text-slate-700 dark:text-ink-soft">
                <span class="font-medium">{{ s.action }}</span>
                @if (s.category) {
                <span
                  class="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-white/5 dark:text-ink-soft/70"
                  title="The content category the model chose for this question">{{ s.category }}</span>
                }
                <span
                  [class]="s.failed ? 'text-red-500' : s.empty ? 'text-amber-600 dark:text-amber-300' : 'text-slate-500 dark:text-ink-soft/70'">
                  — {{ s.outcome }}</span>
              </p>
              <p class="mt-0.5 text-[11px] leading-relaxed text-slate-400">{{ s.roleNote }}</p>
              @if (s.fellBack) {
              <p class="mt-0.5 text-[11px] leading-relaxed text-slate-400">Nothing matched, so it searched again below.
              </p>
              }
              @if (technical()) {
              <p class="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] tabular-nums text-slate-400">
                <span
                  class="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-slate-500 dark:bg-white/5 dark:text-ink-soft/70">{{
                  s.filterText }}</span>
                @if (s.limit != null) {<span>asked for {{ s.limit }}</span>}
                <span>{{ s.hitCount }} back</span>
                <span>{{ msText(s.ms) }}</span>
                @if (s.status) {<span>{{ s.status }}</span>}
              </p>
              }
            </div>
          </div>

          <!-- what came back -->
          @if (s.hits.length) {
          <ul class="mt-2 space-y-1.5 sm:pl-7">
            @for (h of s.hits; track $index; let hi = $index) {
            <li class="rounded-lg border border-[#EAEAEA] px-3 py-2 dark:border-line/70"
              [class]="h.sentToAi ? 'bg-emerald-50/30 dark:bg-emerald-500/[0.04]' : ''">
              <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span class="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10"
                  [title]="'similarity ' + h.scorePct + '%'">
                  <span class="block h-full rounded-full" [class]="scoreTone(h.scorePct)"
                    [style.width.%]="h.scorePct"></span>
                </span>
                <span class="text-[11px] font-medium text-slate-600 dark:text-ink-soft/80">{{ h.matchLabel }}</span>
                <span class="text-[11px] tabular-nums text-slate-400">{{ h.scorePct }}%</span>
                @if (h.sentToAi) {
                <span
                  class="flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300"
                  title="{{ agentName() }} read this before answering">
                  <svg class="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  given to {{ agentName() }}
                </span>
                }
                @if (h.duplicate) {
                <span
                  class="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:bg-amber-500/10 dark:text-amber-300"
                  title="The same passage also came back from another document in this result set">duplicate</span>
                }
              </div>
              <p class="mt-1 text-xs font-medium text-slate-700 dark:text-ink-soft">{{ h.title }}</p>
              @if (h.subtitle) {
              <p class="text-[10px] text-slate-400">{{ h.subtitle }}</p>
              }
              @if (isTextOpen(li + '-' + s.index + '-' + hi)) {
              <p
                class="scrollbar-theme mt-1.5 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-2.5 text-[11px] leading-relaxed text-slate-600 dark:bg-white/5 dark:text-ink-soft/80">{{ h.fullText }}</p>
              @if (technical() && h.fields.length) {
              <div class="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                @for (f of h.fields; track f.key) {
                <span class="text-[10px] text-slate-400"><span class="font-mono">{{ f.key }}</span>: {{ f.value
                  }}</span>
                }
              </div>
              }
              } @else if (h.snippet) {
              <p class="mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-ink-soft/60">{{ h.snippet }}</p>
              }
              @if (h.fullText) {
              <button type="button"
                class="mt-1.5 rounded-md border border-[#EAEAEA] px-2 py-0.5 text-[10px] font-medium text-slate-500 transition hover:border-indigo-300 hover:text-indigo-600 dark:border-line dark:text-ink-soft/70 dark:hover:border-line-strong"
                (click)="toggleText(li + '-' + s.index + '-' + hi)">{{ isTextOpen(li + '-' + s.index + '-' + hi) ?
                'Hide full text' : 'Read full text' }}</button>
              }
            </li>
            }
          </ul>
          }

          @if (technical()) {
          <button type="button"
            class="mt-2 rounded-md border border-[#EAEAEA] px-2 py-0.5 text-[10px] font-medium text-slate-500 transition hover:border-indigo-300 hover:text-indigo-600 dark:border-line dark:text-ink-soft/70 dark:hover:border-line-strong sm:ml-7"
            (click)="toggleRaw(li + '-' + s.index)">{{ isRawOpen(li + '-' + s.index) ? 'Hide' : 'Show' }} raw
            exchange</button>
          @if (isRawOpen(li + '-' + s.index)) {
          <pre
            class="scrollbar-theme mt-2 max-h-80 overflow-auto rounded-lg bg-slate-900 p-3 text-[10px] leading-relaxed text-slate-200 sm:ml-7">{{ s.raw }}</pre>
          }
          }
        </div>
        }
      </div>

      <!-- right: what came of it -->
      <div class="space-y-3 bg-white px-4 py-3 dark:bg-surface-1">

        <div>
          <h4 class="text-[10px] font-semibold uppercase tracking-widest text-slate-400">What {{ agentName() }} said next</h4>
          @if (row.answer) {
          <p class="mt-1 text-[11px] leading-relaxed text-slate-600 dark:text-ink-soft/80">“{{ row.answer.text }}”</p>
          <p class="mt-1 text-[10px] text-slate-400">
            {{ row.answer.time }}
            @if (row.answer.inferred) {
            <span
              title="Matched to the first thing {{ agentName() }} said after this lookup. Newer calls record the turn directly, which will make this exact.">·
              matched by timing</span>
            }
          </p>
          } @else {
          <p class="mt-1 text-[11px] leading-relaxed text-slate-400">{{ agentName() }} said nothing after this lookup.</p>
          }
        </div>

        <!-- claim attribution -->
        <div class="border-t border-[#EAEAEA] pt-3 dark:border-line">
          <h4 class="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Where the answer came from</h4>

          @if (isAnalysing(li)) {
          <p class="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500 dark:text-ink-soft/70">
            <span class="h-3 w-3 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"></span>
            Checking each sentence against the knowledge…
          </p>
          } @else if (row.audit) {
          <ul class="mt-1.5 space-y-2">
            @for (c of row.audit.claims; track $index) {
            <li class="rounded-lg border border-[#EAEAEA] px-2.5 py-2 dark:border-line/70">
              <p class="text-[11px] leading-relaxed text-slate-600 dark:text-ink-soft/80">“{{ c.claim }}”</p>
              <p class="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]">
                <span class="font-medium" [class]="verdictTone(c.verdict)">{{ verdictLabel(c.verdict) }}</span>
                @for (n of c.chunks; track $index) {
                @let chunk = chunkAt(row.sentChunks, n);
                <span class="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-500 dark:bg-white/5 dark:text-ink-soft/70"
                  [title]="(chunk?.title || '') + ' · ' + (chunk?.subtitle || '')">{{
                  chunk?.title || 'unknown' }}@if (chunk?.subtitle; as subtitle) {<span
                    class="text-slate-400"> · {{ subtitle }}</span>}</span>
                }
              </p>
              @if (c.note) {
              <p class="mt-0.5 text-[10px] text-slate-400">{{ c.note }}</p>
              }
            </li>
            }
          </ul>
          @if (!row.audit.claims.length) {
          <p class="mt-1.5 text-[11px] text-slate-400">{{ agentName() }} made no factual claims here.</p>
          }
          <div class="mt-2 flex flex-wrap items-center gap-2">
            @if (canAnalyse(li)) {
            <button type="button"
              class="rounded-lg border border-[#EAEAEA] px-2.5 py-1 text-[10px] font-medium text-slate-600 transition hover:border-indigo-300 hover:text-indigo-600 dark:border-line dark:text-ink-soft dark:hover:border-line-strong"
              (click)="analyse(li)">Check again</button>
            }
            @if (row.audit.analysedAt) {
            <span class="text-[10px] text-slate-400">Checked {{ row.audit.analysedAt | date: 'short' }}</span>
            }
          </div>
          <p class="mt-1.5 text-[10px] leading-relaxed text-slate-400">“Not in the knowledge” usually means the
            knowledge base is missing something — {{ agentName() }} is also guided by its own instructions, so it is not
            automatically a mistake.</p>
          } @else if (auditError(li)) {
          <p class="mt-1.5 text-[11px] text-red-500">{{ auditError(li) }}</p>
          <button type="button"
            class="mt-1.5 rounded-lg bg-indigo-500 px-3 py-1.5 text-[11px] font-medium text-white transition hover:bg-indigo-600"
            (click)="analyse(li)">Try again</button>
          } @else if (canAnalyse(li)) {
          <p class="mt-1.5 text-[11px] leading-relaxed text-slate-500 dark:text-ink-soft/70">Check what {{ agentName() }} said
            against the {{ row.sentChunks.length }} pieces of knowledge it was given, sentence by sentence, to find the
            document behind a wrong answer.</p>
          <button type="button"
            class="mt-2 flex items-center gap-1.5 rounded-lg bg-indigo-500 px-3 py-1.5 text-[11px] font-medium text-white transition hover:bg-indigo-600 disabled:opacity-40"
            (click)="analyse(li)">
            <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            Check this answer
          </button>
          } @else {
          <p class="mt-1.5 text-[11px] leading-relaxed text-slate-400">Nothing to check — this lookup has no recorded
            answer, or no knowledge reached {{ agentName() }}.</p>
          }
        </div>

        @if (row.slide) {
        <div class="border-t border-[#EAEAEA] pt-3 dark:border-line">
          <h4 class="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Slide put on screen</h4>
          @if (row.slide.winnerTitle) {
          <p class="mt-1 flex flex-wrap items-baseline gap-x-2 text-[11px] text-slate-600 dark:text-ink-soft/80">
            <span class="font-medium">{{ row.slide.winnerTitle }}</span>
            @if (row.slide.shown === 'shown') {
            <span
              class="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">the
              prospect saw this</span>
            } @else if (row.slide.shown === 'not-shown') {
            <span
              class="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:bg-amber-500/10 dark:text-amber-300"
              title="This page won the slide search but never appears in the demo's slide history">chosen, but never
              appeared</span>
            }
          </p>
          } @else {
          <p class="mt-1 text-[11px] font-medium text-slate-600 dark:text-ink-soft/80">No slide was put on screen</p>
          }
          <p class="mt-1 text-[10px] leading-relaxed text-slate-400">{{ row.slide.ruleText }}</p>
          @if (row.slide.candidates.length) {
          <p class="mt-2 text-[10px] font-medium text-slate-400">It chose from:</p>
          <ul class="mt-1 space-y-1">
            @for (c of row.slide.candidates; track $index) {
            <li class="flex items-baseline gap-2 text-[10px]" [class.opacity-50]="!c.eligible">
              <span class="w-8 shrink-0 tabular-nums text-slate-500 dark:text-ink-soft/70">{{ c.score != null ?
                ((c.score * 100) | number: '1.0-0') + '%' : '—' }}</span>
              <span class="min-w-0 flex-1 truncate"
                [class]="c.winner ? 'font-medium text-slate-700 dark:text-ink-soft' : 'text-slate-400'"
                [title]="c.group + ' · ' + c.key">{{ c.title || c.key }}</span>
              @if (c.winner) {
              <span class="shrink-0 font-medium text-emerald-600 dark:text-emerald-300">chosen</span>
              } @else if (!c.eligible) {
              <span class="shrink-0 text-slate-400">too weak</span>
              }
            </li>
            }
          </ul>
          }
        </div>
        }

        <!-- Only when there is something to show: calls that ran before this was captured
             would otherwise repeat the same placeholder under every single lookup. -->
        @if (row.served) {
        <div class="border-t border-[#EAEAEA] pt-3 dark:border-line">
          <h4 class="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Exact text {{ agentName() }} was given</h4>
          <p class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-slate-400">
            @if (row.served.source) {
            <span
              class="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-slate-500 dark:bg-white/5 dark:text-ink-soft/70">{{
              row.served.source }}</span>
            }
            @if (row.served.chars) {
            <span class="tabular-nums">{{ row.served.chars }} characters</span>
            }
            @if (row.served.truncated) {
            <span class="text-amber-500">stored shortened</span>
            }
            @if (row.served.disabled) {
            <span class="text-amber-500">knowledge base was off</span>
            }
          </p>
          @if (row.served.slide?.title) {
          <p class="mt-1 text-[10px] text-slate-400">Slide alongside: {{ row.served.slide?.title }}</p>
          }
          <button type="button"
            class="mt-1.5 rounded-md border border-[#EAEAEA] px-2 py-0.5 text-[10px] font-medium text-slate-500 transition hover:border-indigo-300 hover:text-indigo-600 dark:border-line dark:text-ink-soft/70 dark:hover:border-line-strong"
            (click)="toggleText(li + '-served')">{{ isTextOpen(li + '-served') ? 'Hide' : 'Read' }} the text handed to
            the AI</button>
          @if (isTextOpen(li + '-served')) {
          <p
            class="scrollbar-theme mt-1.5 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-2.5 text-[11px] leading-relaxed text-slate-600 dark:bg-white/5 dark:text-ink-soft/80">{{ row.served.text }}</p>
          }
        </div>
        }
      </div>
    </div>
    }
  </div>
  }
</div>
}
